import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Text,
} from "@chakra-ui/react";
import { PRICING_API_URL } from "@umami/chains";
import { type ReactNode, useState } from "react";

import { EyeIcon, EyeOffIcon } from "../../assets/icons";

// We check if we are in staging environment by checking for VITE_IS_STAGING flag
// This flag should be set to "true" in the staging environment configuration
const isStagingEnv = import.meta.env.VITE_IS_STAGING === "true";

interface StagingPasswordProtectionProps {
  children: ReactNode;
}

export const StagingPasswordProtection = ({ children }: StagingPasswordProtectionProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    sessionStorage.getItem("staging_authenticated") === "true"
  );
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  // If not staging, render children directly
  if (!isStagingEnv) {
    return children;
  }

  // If authenticated, render children
  if (isAuthenticated) {
    return children;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsValidating(true);

    try {
      const response = await fetch(`${PRICING_API_URL}/staging/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsAuthenticated(true);
        // Store simple flag in sessionStorage so it persists during the session
        sessionStorage.setItem("staging_authenticated", "true");
      } else {
        setError("Incorrect password");
      }
    } catch (err) {
      console.error(err);
      setError("Validation failed. Please try again.");
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <Flex
      position="fixed"
      zIndex={9999}
      top={0}
      left={0}
      alignItems="center"
      justifyContent="center"
      width="100vw"
      height="100vh"
      backgroundColor="black"
    >
      <Box
        minWidth="300px"
        padding="2rem"
        border="1px solid"
        borderColor="gray.600"
        borderRadius="8px"
        backgroundColor="gray.800"
      >
        <Heading marginBottom="1.5rem" color="white" textAlign="center" size="lg">
          Staging Environment
        </Heading>
        <Text marginBottom="1.5rem" color="gray.300" textAlign="center" size="md">
          Please enter the password to access the staging environment
        </Text>
        <form onSubmit={handleSubmit}>
          <InputGroup marginBottom="1rem">
            <Input
              width="100%"
              padding="0.75rem"
              paddingRight="60px"
              color="white"
              fontSize="1rem"
              border="1px solid"
              borderColor="gray.500"
              borderRadius="4px"
              _hover={{
                borderColor: "blue.400",
                backgroundColor: "gray.700",
              }}
              _focus={{
                borderColor: "blue.400",
                boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
              }}
              _placeholder={{
                color: "gray.400",
              }}
              autoFocus
              backgroundColor="gray.700"
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter password"
              type={showPassword ? "text" : "password"}
              value={password}
            />
            <InputRightElement>
              <IconButton
                color="gray.400"
                _hover={{
                  color: "gray.300",
                }}
                aria-label="Toggle password visibility"
                icon={
                  showPassword ? (
                    <EyeOffIcon width="24px" data-testid="eye-off-icon" />
                  ) : (
                    <EyeIcon width="24px" data-testid="eye-icon" />
                  )
                }
                onClick={() => setShowPassword(val => !val)}
                tabIndex={-1}
                variant="unstyled"
              />
            </InputRightElement>
          </InputGroup>
          {error && (
            <Text marginBottom="1rem" color="red.400" textAlign="center" size="sm">
              {error}
            </Text>
          )}
          <Button
            width="100%"
            padding="0.75rem"
            color="black"
            fontSize="1rem"
            border="none"
            borderRadius="4px"
            _hover={{
              backgroundColor: "blue.600",
            }}
            _active={{
              backgroundColor: "blue.700",
            }}
            backgroundColor="blue.500"
            isLoading={isValidating}
            type="submit"
          >
            Access Staging
          </Button>
        </form>
      </Box>
    </Flex>
  );
};
